/* ------------------------------------------------------------------
  File: Tabs.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable tabs component for the Accend Holdings website.
//   - Supports tabbed content sections, default active tab, icons,
//     visual variants, size options, alignment, accessible tab controls,
//     and dark-surface compatibility.
//   - Uses the acc-tabs namespace to avoid conflicts with Bootstrap.
//   - Suitable for service categories, business sectors, investor content,
//     newsroom filters, project categories, and grouped page content.

/**
 * Tabs
 *
 * A reusable tabs component for organizing related content.
 *
 * @param {Array} items
 * Array of tab items.
 *
 * Each item should support:
 * - id: string
 * - label: React.ReactNode
 * - content: React.ReactNode
 * - icon: React.ReactNode
 *
 * @param {string} [defaultActiveId]
 * ID of the tab that should be active by default.
 *
 * @param {"default" | "pills" | "underline" | "boxed" | "inverse"} [variant="default"]
 * Controls the visual style of the tabs.
 *
 * @param {"sm" | "md" | "lg"} [size="md"]
 * Controls the tab button size.
 *
 * @param {"left" | "center" | "between"} [align="left"]
 * Controls tab list alignment.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import { useId, useState } from "react";
import "./Tabs.css";

const Tabs = ({
  items = [],
  defaultActiveId,
  variant = "default",
  size = "md",
  align = "left",
  className = "",
  ...props
}) => {
  const generatedId = useId();

  const firstItemId = items[0]?.id;
  const initialActiveId = defaultActiveId || firstItemId;

  const [activeTabId, setActiveTabId] = useState(initialActiveId);

  const tabsClasses = [
    "acc-tabs",
    `acc-tabs--${variant}`,
    `acc-tabs--${size}`,
    `acc-tabs--${align}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (!items.length) {
    return null;
  }

  const activeItem = items.find((item) => item.id === activeTabId) || items[0];

  return (
    <div className={tabsClasses} {...props}>
      <div className="acc-tabs__list-wrap">
        <div className="acc-tabs__list" role="tablist">
          {items.map((item) => {
            const isActive = item.id === activeItem.id;
            const tabId = `tabs-${generatedId}-${item.id}`;
            const panelId = `tabs-${generatedId}-${item.id}-panel`;

            return (
              <button
                key={item.id}
                id={tabId}
                type="button"
                role="tab"
                className={[
                  "acc-tabs__trigger",
                  isActive ? "acc-tabs__trigger--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-selected={isActive}
                aria-controls={panelId}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveTabId(item.id)}
              >
                {item.icon && (
                  <span className="acc-tabs__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                )}

                <span className="acc-tabs__label">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {items.map((item) => {
        const isActive = item.id === activeItem.id;
        const tabId = `tabs-${generatedId}-${item.id}`;
        const panelId = `tabs-${generatedId}-${item.id}-panel`;

        return (
          <div
            key={item.id}
            id={panelId}
            role="tabpanel"
            className="acc-tabs__panel"
            aria-labelledby={tabId}
            hidden={!isActive}
          >
            {item.content}
          </div>
        );
      })}
    </div>
  );
};

export default Tabs;